import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';

// Importar estilos CSS para el Logo
import '../../styles/logo.css';

/**
 * Reusable Logo component for VV Health
 * Componente Logo reutilizable para VV Health
 * 
 * @param {Object} props - Component properties / Propiedades del componente
 * @param {string} props.size - Logo size: 'small', 'medium', 'large' (default: 'medium') / Tamaño del logo: 'small', 'medium', 'large' (por defecto: 'medium')
 * @param {string} props.className - Additional CSS classes / Clases CSS adicionales
 * @param {boolean} props.showText - Whether to show text next to logo (default: false) / Indica si se debe mostrar el texto junto al logo (por defecto: false)
 * @returns {JSX.Element} Logo component / Componente Logo
 */
const Logo = ({ size = 'medium', className = '', showText = false }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Determine logo size based on size prop and window width
  // Determinar el tamaño del logo según la prop size y el ancho de la ventana
  const getLogoSize = useCallback(() => {
    // Base sizes for different breakpoints / Tamaños base para diferentes breakpoints
    const sizes = {
      small: { mobile: 32, tablet: 38, desktop: 40 },
      medium: { mobile: 45, tablet: 60, desktop: 80 },
      large: { mobile: 60, tablet: 90, desktop: 120 }
    };
    
    // Determine current breakpoint / Determinar el breakpoint actual
    const breakpoint = windowWidth < 576 ? 'mobile' : windowWidth < 992 ? 'tablet' : 'desktop';
    
    // Return corresponding size / Retornar el tamaño correspondiente
    return sizes[size][breakpoint];
  }, [windowWidth, size]);
  
  // Update window width when it changes
  // Actualizar el ancho de la ventana cuando cambia
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Memoize logo size to prevent recalculation on every render
  // Memorizar el tamaño del logo para evitar recálculos en cada renderizado
  const logoSize = useMemo(() => getLogoSize(), [getLogoSize]);
  
  // Memoize logo content to prevent unnecessary re-renders
  // Memorizar el contenido del logo para evitar re-renderizados innecesarios
  const logoContent = useMemo(() => (
    <div className={className}>
      <img 
        src="/images/logo.png" 
        alt="VV Health Logo" 
        width="auto"
        height={logoSize}
        className="d-inline-block align-top"
        loading="eager" // Cargar el logo con alta prioridad
      />
      {showText && (
        <span className="logo-text ms-2">
          VV HEALTH
        </span>
      )}
    </div>
  ), [className, logoSize, showText]);
  
  return logoContent;
};

Logo.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  className: PropTypes.string,
  showText: PropTypes.bool
};

// Memoize the component to prevent unnecessary re-renders
// Memorizar el componente para evitar renderizados innecesarios
export default memo(Logo);
